'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from 'convex/react';
import { useLocale } from 'next-intl';
import { useIsPresent } from 'framer-motion';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { FloatingVoiceDock } from '@/components/voice/floating-voice-dock';
import type { VoiceAvatarLook } from '@/components/voice/voice-companion-face';
import { useYourImaiPersona } from '@/components/your-imai/use-your-imai-persona';
import { getYourImaiCopy } from '@/i18n/your-imai-copy';
import { useRealtimeVoiceCallController } from '@/components/org-chat/use-realtime-voice-call-controller';
import { useRealtimeVoiceThreadDispatch } from '@/components/org-chat/use-realtime-voice-thread-dispatch';
import type { OrgChatQueueItem } from '@/components/org-chat/org-chat-queue-controls';
import { useVoiceCallCreditGate } from '@/components/org-chat/use-voice-call-credit-gate';
import {
  getPersistentVoiceCallHref,
  getVoiceCallTopUpHref,
  type PersistentVoiceCall,
} from '@/lib/persistent-voice-call-state';
import {
  buildRealtimeVoicePageContext,
  formatRealtimeVoicePageContext,
  type RealtimeVoicePageMetadata,
} from '@/lib/realtime-voice-page-context';
import { getRealtimeVoicePendingQuestionRevision } from '@/lib/realtime-voice-pending-question-revision';

type Props = {
  call: PersistentVoiceCall;
  pathname: string;
  pageMetadata: RealtimeVoicePageMetadata | null;
  onEnd: () => void;
  onEnding: (sessionId: string) => void;
  dockTarget?: HTMLElement | null;
};

export function PersistentVoiceCallSession(props: Props) {
  const { onEnd } = props;
  const orgThreadId = props.call.orgThreadId as Id<'orgThreads'> | undefined;
  const locale = useLocale();
  // The call avatar wears the member's Your IMAI look, live as they edit it.
  const { persona } = useYourImaiPersona(
    props.call.orgSlug,
    getYourImaiCopy(locale).imaiName
  );
  const thread = useQuery(
    api.organizations.threads.queries.getById,
    orgThreadId ? { id: orgThreadId } : 'skip'
  );
  useEffect(() => {
    if (thread === null) onEnd();
  }, [thread, onEnd]);
  if (!thread || !orgThreadId) {
    const sourceVisible =
      props.pathname === getPersistentVoiceCallHref(props.call);
    const docked =
      props.call.sourceKind === 'your-imai' &&
      sourceVisible &&
      props.dockTarget?.isConnected;
    const dock = (
      <FloatingVoiceDock
        locale={locale}
        status="connecting"
        look={persona}
        inputMode="vad"
        micMuted={false}
        pttActive={false}
        canPressToSpeak={false}
        isSourceChatVisible={sourceVisible}
        docked={Boolean(docked)}
        sourceThreadHref={getPersistentVoiceCallHref(props.call)}
        positionKey={props.call.positionKey}
        sourceThreadTitle={props.call.title}
        callElapsedMs={0}
        onToggleMute={() => {}}
        onInputModeChange={() => {}}
        onPressToSpeakStart={() => {}}
        onPressToSpeakEnd={() => {}}
        onEnd={onEnd}
      />
    );
    return docked && props.dockTarget
      ? createPortal(dock, props.dockTarget)
      : dock;
  }
  return (
    <ConnectedVoiceCall
      {...props}
      orgThreadId={orgThreadId}
      thread={thread}
      look={persona}
    />
  );
}

function ConnectedVoiceCall({
  call,
  pathname,
  pageMetadata,
  onEnd,
  onEnding,
  orgThreadId,
  thread,
  dockTarget,
  look,
}: Props & {
  look: VoiceAvatarLook;
  orgThreadId: Id<'orgThreads'>;
  thread: NonNullable<
    ReturnType<
      typeof useQuery<typeof api.organizations.threads.queries.getById>
    >
  >;
}) {
  const locale = useLocale();
  const handleEnding = useCallback(
    () => onEnding(call.sessionId),
    [onEnding, call.sessionId]
  );
  const isPresent = useIsPresent();
  const levelsRef = useRef({ user: 0, agent: 0 });
  const delegationTarget = useRealtimeVoiceThreadDispatch({
    orgThreadId,
    orgId: call.organizationId as Id<'organizations'>,
    sourceKind: call.sourceKind,
  });
  const pageContext = useMemo(() => {
    const context = buildRealtimeVoicePageContext({
      pathname,
      sourceOrgSlug: call.orgSlug,
      sourceThreadId: String(orgThreadId),
      pageMetadata,
    });
    return context ? formatRealtimeVoicePageContext(context) : null;
  }, [pathname, call.orgSlug, orgThreadId, pageMetadata]);
  const realtimeStatus =
    thread.realtimeVoiceStatusText && thread.realtimeVoiceStatusTurnId
      ? {
          text: thread.realtimeVoiceStatusText,
          turnId: thread.realtimeVoiceStatusTurnId,
          sessionId: thread.realtimeVoiceStatusSessionId,
          createdAt: thread.realtimeVoiceStatusCreatedAt,
        }
      : null;
  const controller = useRealtimeVoiceCallController({
    open: isPresent,
    onClose: onEnd,
    onEnding: handleEnding,
    sessionId: call.sessionId,
    sourceKind: call.sourceKind,
    orgThreadId,
    orgSlug: call.orgSlug,
    organizationId: call.organizationId as Id<'organizations'>,
    delegationTarget,
    queuedMessages: (thread.messageQueue ?? []) as OrgChatQueueItem[],
    realtimeStatus,
    pendingQuestionRevision: getRealtimeVoicePendingQuestionRevision(thread),
    levelsRef,
    pageContext,
    isSourceChatVisible: pathname === getPersistentVoiceCallHref(call),
    floating: true,
  });
  const creditGate = useVoiceCallCreditGate(call.orgSlug);
  const errorText = controller.errorText ?? controller.setupError;
  const docked =
    call.sourceKind === 'your-imai' &&
    pathname === getPersistentVoiceCallHref(call) &&
    dockTarget?.isConnected;
  const dock = (
    <FloatingVoiceDock
      locale={locale}
      status={controller.status}
      look={look}
      toolActivity={controller.toolActivity}
      isEnding={controller.isEnding}
      inputMode={controller.inputMode}
      micMuted={controller.micMuted}
      pttActive={controller.pttActive}
      canPressToSpeak={
        controller.status === 'listening' || controller.status === 'speaking'
      }
      isSourceChatVisible={pathname === getPersistentVoiceCallHref(call)}
      sourceThreadHref={getPersistentVoiceCallHref(call)}
      docked={Boolean(docked)}
      positionKey={call.positionKey}
      sourceThreadTitle={thread.title ?? call.title}
      callElapsedMs={controller.callElapsedMs}
      errorText={errorText}
      topUpHref={getVoiceCallTopUpHref({
        orgSlug: call.orgSlug,
        errorText,
        lowCreditMessage: creditGate.message,
      })}
      onToggleMute={() => controller.setMicMuted((muted) => !muted)}
      onInputModeChange={(mode) => {
        controller.setPttActive(false);
        controller.setIsCapturing(false);
        controller.setMicMuted(false);
        controller.setInputMode(mode);
      }}
      onPressToSpeakStart={() => {
        controller.setPttActive(true);
        controller.setIsCapturing(true);
      }}
      onPressToSpeakEnd={() => {
        controller.setPttActive(false);
        controller.setIsCapturing(false);
      }}
      onEnd={controller.handleEnd}
    />
  );
  return docked && dockTarget ? createPortal(dock, dockTarget) : dock;
}
